import { useCallback, useEffect, useState } from "react";

import { HttpApiService } from "@/shared/services/httpApi.service";

import { ClientsService } from "../services/clientsService";
import type { OrganizationDetailResponse } from "../types/clients-api.types";

export interface UseOrganizationSummaryReturn {
  organization: OrganizationDetailResponse | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Context header data for the level-2 drill-down: GET /internal/clients/organizations/:id. Provides the
 * organization identity (name, email, country) plus the client/account counters shown as summary chips.
 */
export function useOrganizationSummary(organizationId: string | undefined): UseOrganizationSummaryReturn {
  const [organization, setOrganization] = useState<OrganizationDetailResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSummary = useCallback(async () => {
    if (!organizationId) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await ClientsService.fetchOrganizationById(organizationId);
      setOrganization(response.data);
    } catch (err) {
      setError(HttpApiService.getErrorMessage(err));
      setOrganization(null);
    } finally {
      setIsLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  return { organization, isLoading, error, refetch: fetchSummary };
}
